import { lazy, Suspense, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import { useQuery } from '@tanstack/react-query';
import {
  listFiles,
  fetchFilePreview,
  getDownloadUrl,
  type FileEntry,
} from '../../api/fileBrowser';

const MonacoEditor = lazy(() => import('@monaco-editor/react'));

const TEXT_EXTENSIONS = new Set([
  'txt', 'log', 'json', 'cfg', 'ini', 'yaml', 'yml',
  'toml', 'xml', 'conf', 'properties', 'env', 'md',
]);
const MAX_PREVIEW_BYTES = 1 * 1024 * 1024;

function ext(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
}

function fmtSize(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function pathJoin(base: string, name: string): string {
  return base ? `${base}/${name}` : name;
}

interface PreviewDialogProps {
  gameId: string;
  filePath: string;
  fileName: string;
  size: number;
  onClose: () => void;
}

function PreviewDialog({ gameId, filePath, fileName, size, onClose }: PreviewDialogProps) {
  const { data: content, isLoading, isError } = useQuery({
    queryKey: ['fileBrowser', 'preview', gameId, filePath],
    queryFn: () => fetchFilePreview(gameId, filePath),
    retry: false,
  });

  const language = (() => {
    const e = ext(fileName);
    if (e === 'json') return 'json';
    if (e === 'yaml' || e === 'yml') return 'yaml';
    if (e === 'xml') return 'xml';
    if (e === 'ini' || e === 'cfg' || e === 'conf' || e === 'properties') return 'ini';
    if (e === 'md') return 'markdown';
    return 'plaintext';
  })();

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontFamily: 'monospace' }}>{fileName}</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              href={getDownloadUrl(gameId, filePath)}
              download={fileName}
              component="a"
            >
              Download
            </Button>
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: 520 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}
        {isError && (
          <Box sx={{ p: 3 }}>
            <Typography color="error">Failed to load file preview.</Typography>
          </Box>
        )}
        {content !== undefined && (
          <Suspense fallback={<CircularProgress sx={{ m: 3 }} />}>
            <MonacoEditor
              height="100%"
              language={language}
              value={content}
              options={{
                readOnly: true,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 13,
                wordWrap: 'on',
              }}
              theme="vs-dark"
            />
          </Suspense>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 2 }}>
          {fmtSize(size)}
        </Typography>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

interface Props {
  gameId: string;
  gameName: string;
  open: boolean;
  onClose: () => void;
}

export function FileBrowserDrawer({ gameId, gameName, open, onClose }: Props) {
  const [currentPath, setCurrentPath] = useState('');
  const [filter, setFilter] = useState('');
  const [preview, setPreview] = useState<{ path: string; name: string; size: number } | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['fileBrowser', gameId, currentPath],
    queryFn: () => listFiles(gameId, currentPath),
    enabled: open,
  });

  const entries = data?.entries ?? [];
  const filtered = filter
    ? entries.filter((e) => e.name.toLowerCase().includes(filter.toLowerCase()))
    : entries;

  const pathParts = currentPath ? currentPath.split('/') : [];

  function navigate(newPath: string) {
    setCurrentPath(newPath);
    setFilter('');
  }

  function handleEntryClick(entry: FileEntry) {
    if (entry.type === 'dir') {
      navigate(pathJoin(currentPath, entry.name));
      return;
    }
    const filePath = pathJoin(currentPath, entry.name);
    const canPreview = TEXT_EXTENSIONS.has(ext(entry.name)) && (entry.size ?? Infinity) < MAX_PREVIEW_BYTES;
    if (canPreview) {
      setPreview({ path: filePath, name: entry.name, size: entry.size ?? 0 });
    } else {
      const a = document.createElement('a');
      a.href = getDownloadUrl(gameId, filePath);
      a.download = entry.name;
      a.click();
    }
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 640 } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
          >
            <Typography variant="h6">Files — {gameName}</Typography>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Stack>

          {/* Breadcrumbs */}
          <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Breadcrumbs>
              <Link
                component="button"
                underline="hover"
                color={currentPath === '' ? 'text.primary' : 'inherit'}
                onClick={() => navigate('')}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
              >
                <HomeIcon fontSize="small" />
                Root
              </Link>
              {pathParts.map((part, idx) => {
                const partPath = pathParts.slice(0, idx + 1).join('/');
                const isLast = idx === pathParts.length - 1;
                return isLast ? (
                  <Typography key={partPath} color="text.primary" variant="body2">{part}</Typography>
                ) : (
                  <Link
                    key={partPath}
                    component="button"
                    underline="hover"
                    onClick={() => navigate(partPath)}
                    sx={{ cursor: 'pointer' }}
                    variant="body2"
                  >
                    {part}
                  </Link>
                );
              })}
            </Breadcrumbs>
          </Box>

          {/* Filter */}
          <Box sx={{ px: 2, py: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Filter…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              }}
            />
          </Box>

          {/* File table */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {isError && (
              <Box sx={{ p: 3 }}>
                <Typography color="error">Failed to load directory.</Typography>
              </Box>
            )}
            {!isLoading && !isError && (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell align="right">Modified</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {filter ? 'No matches' : 'Empty directory'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {filtered.map((entry) => {
                    const filePath = pathJoin(currentPath, entry.name);
                    const canPreview = entry.type === 'file' &&
                      TEXT_EXTENSIONS.has(ext(entry.name)) &&
                      (entry.size ?? Infinity) < MAX_PREVIEW_BYTES;
                    return (
                      <TableRow
                        key={entry.name}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleEntryClick(entry)}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {entry.type === 'dir'
                              ? <FolderIcon fontSize="small" sx={{ color: 'warning.main' }} />
                              : <InsertDriveFileIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                            }
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {entry.name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="text.secondary">
                            {fmtSize(entry.size)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="text.secondary">
                            {fmtDate(entry.mtime)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          {entry.type === 'file' && (
                            <Tooltip title={canPreview ? 'Preview' : 'Download'}>
                              <IconButton
                                size="small"
                                href={canPreview ? undefined : getDownloadUrl(gameId, filePath)}
                                download={canPreview ? undefined : entry.name}
                                component={canPreview ? 'button' : 'a'}
                                onClick={canPreview ? () => setPreview({ path: filePath, name: entry.name, size: entry.size ?? 0 }) : undefined}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
        </Box>
      </Drawer>

      {preview && (
        <PreviewDialog
          gameId={gameId}
          filePath={preview.path}
          fileName={preview.name}
          size={preview.size}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
}
